import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

import { DossierId } from '../../../../../resources/dossier/types';
import {
    useGetTrackedTripProperties,
    useTrackEventAmplitude,
} from '../../../../../shared/analytics';
import { useSidePanel } from '../../../SidePanel/SidePanelContext';

type UseAudioPlayerProps = {
    audioUrl: string;
    isDeleted: boolean;
    phoneCallId: number;
};

const formatTime = (seconds: number): string => {
    if (!isFinite(seconds) || seconds < 0) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
};

const extractSliderValue = (value: number | number[]): number => {
    return Array.isArray(value) ? value[0] : value;
};

/**
 * Custom hook for managing audio player state and interactions.
 * Handles all audio playback logic including play/pause, seeking, volume control,
 * and keyboard interactions. Integrates with Amplitude analytics to track when
 * recordings are played.
 */
export const useAudioPlayer = ({ audioUrl, isDeleted, phoneCallId }: UseAudioPlayerProps) => {
    const audioRef = useRef<HTMLAudioElement>(null);

    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [volume, setVolume] = useState(1);
    const [previousVolume, setPreviousVolume] = useState(1);

    const { dossierId } = useParams() as { dossierId: DossierId };
    const { trackEvent } = useTrackEventAmplitude();
    const trackedTripProperties = useGetTrackedTripProperties(dossierId);
    const { isOpen } = useSidePanel();

    const isReady = duration > 0;

    // Set up audio event listeners
    useEffect(() => {
        const audio = audioRef.current;
        if (!audio) return;

        const onLoadedMetadata = () => setDuration(audio.duration || 0);
        const onTimeUpdate = () => setCurrentTime(audio.currentTime || 0);
        const onEnded = () => {
            setIsPlaying(false);
            setCurrentTime(0);
        };
        const onPlay = () => setIsPlaying(true);
        const onPause = () => setIsPlaying(false);

        audio.addEventListener('loadedmetadata', onLoadedMetadata);
        audio.addEventListener('timeupdate', onTimeUpdate);
        audio.addEventListener('ended', onEnded);
        audio.addEventListener('play', onPlay);
        audio.addEventListener('pause', onPause);

        return () => {
            audio.removeEventListener('loadedmetadata', onLoadedMetadata);
            audio.removeEventListener('timeupdate', onTimeUpdate);
            audio.removeEventListener('ended', onEnded);
            audio.removeEventListener('play', onPlay);
            audio.removeEventListener('pause', onPause);
        };
    }, []);

    // Reset playback state when audio source changes (derived state during render)
    const [prevAudioSource, setPrevAudioSource] = useState({ url: audioUrl, deleted: isDeleted });
    if (prevAudioSource.url !== audioUrl || prevAudioSource.deleted !== isDeleted) {
        setPrevAudioSource({ url: audioUrl, deleted: isDeleted });
        setIsPlaying(false);
        setCurrentTime(0);
        setDuration(0);
    }

    // Sync DOM when audio source changes
    useEffect(() => {
        const audio = audioRef.current;
        if (!audio) return;

        if (!isDeleted) {
            audio.src = audioUrl;
            audio.load();
        } else {
            audio.removeAttribute('src');
            audio.load();
        }
    }, [audioUrl, isDeleted]);

    const handlePlayPause = useCallback(async () => {
        const audio = audioRef.current;
        if (!audio || isDeleted) return;

        if (!isPlaying) {
            try {
                await audio.play();

                trackEvent((ampli, defaultProperties) => {
                    ampli.recordingListened({
                        ...defaultProperties,
                        ...trackedTripProperties,
                        call_id: phoneCallId.toString(),
                    });
                });
            } catch {
                console.error(
                    `audio player error: unable to play audio for phone call id ${phoneCallId.toString()}`,
                );
                setIsPlaying(false);
            }
        } else {
            audio.pause();
        }
    }, [isPlaying, isDeleted, phoneCallId, trackEvent, trackedTripProperties]);

    const handleSeek = useCallback(
        (_event: Event | React.SyntheticEvent, value: number | number[]) => {
            if (isDeleted) return;
            const newTime = extractSliderValue(value);
            setCurrentTime(newTime);
        },
        [isDeleted],
    );

    const handleSeekCommitted = useCallback(
        (_event: Event | React.SyntheticEvent, value: number | number[]) => {
            const audio = audioRef.current;
            if (!audio || isDeleted) return;
            const newTime = extractSliderValue(value);
            audio.currentTime = newTime;
            setCurrentTime(newTime);
        },
        [isDeleted],
    );

    const handleKeyDown = useCallback(
        (event: React.KeyboardEvent) => {
            if (event.key === ' ' || event.key === 'Enter') {
                event.preventDefault();
                void handlePlayPause();
            }
        },
        [handlePlayPause],
    );

    const handleVolumeChange = useCallback(
        (_event: Event | React.SyntheticEvent, value: number | number[]) => {
            const newVolume = extractSliderValue(value);
            setVolume(newVolume);
            if (audioRef.current) {
                audioRef.current.volume = newVolume;
            }
        },
        [],
    );

    const toggleMute = useCallback(() => {
        if (volume === 0) {
            setVolume(previousVolume);
            if (audioRef.current) {
                audioRef.current.volume = previousVolume;
            }
        } else {
            setPreviousVolume(volume);
            setVolume(0);
            if (audioRef.current) {
                audioRef.current.volume = 0;
            }
        }
    }, [volume, previousVolume]);

    const resetAudioDom = useCallback(() => {
        const audio = audioRef.current;
        if (audio) {
            audio.pause();
            audio.currentTime = 0;
        }
    }, []);

    // Reset playback state when panel closes (derived state during render)
    const [prevIsOpen, setPrevIsOpen] = useState(isOpen);
    if (prevIsOpen !== isOpen && !isOpen) {
        setPrevIsOpen(isOpen);
        setCurrentTime(0);
        setIsPlaying(false);
    }

    // Reset playback state when switching to a different phone call (derived state during render)
    const [prevPhoneCallId, setPrevPhoneCallId] = useState(phoneCallId);
    if (prevPhoneCallId !== phoneCallId) {
        setPrevPhoneCallId(phoneCallId);
        setCurrentTime(0);
        setIsPlaying(false);
    }

    // Reset audio DOM when panel closes
    useEffect(() => {
        if (!isOpen) {
            resetAudioDom();
        }
    }, [isOpen, resetAudioDom]);

    // Reset audio DOM when switching to a different phone call
    useEffect(() => {
        resetAudioDom();
    }, [phoneCallId, resetAudioDom]);

    return {
        audioRef,
        isPlaying,
        currentTime,
        duration,
        volume,
        isReady,
        handlePlayPause,
        handleSeek,
        handleSeekCommitted,
        handleKeyDown,
        handleVolumeChange,
        toggleMute,
        formatTime,
    };
};
